import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, LogOut, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PasswordField } from '../components/fields';
import { setPassword as apiSetPassword } from '../lib/auth';
import { getMe, logout, logoutEverywhere, type Me } from '../lib/session';

type Load = 'loading' | 'ready' | 'unauthenticated' | 'error';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Spec 003 — account settings (SC-013/016). Loads the current identity (silent-refresh on expiry); if
 * unauthenticated, redirects to sign in. Lets a member set/change a password (12+ chars, breach-checked
 * server-side) and log out — this session or everywhere. Four states: loading (spinner) → ready (form)
 * → the "empty" case is the account with no password yet (a distinct prompt) → error. WCAG 2.1 AA.
 */
export default function Account() {
  const navigate = useNavigate();
  const [load, setLoad] = useState<Load>('loading');
  const [me, setMe] = useState<Me | null>(null);
  const [password, setPasswordValue] = useState('');
  const [save, setSave] = useState<SaveStatus>('idle');
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [generalError, setGeneralError] = useState<string | null>(null);
  const savedRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    let active = true;
    getMe().then((r) => {
      if (!active) return;
      if (r.kind === 'ok') {
        setMe(r.user);
        setLoad('ready');
      } else if (r.kind === 'unauthenticated') {
        setLoad('unauthenticated');
      } else {
        setLoad('error');
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (load === 'unauthenticated') navigate('/login', { replace: true });
  }, [load, navigate]);

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    if (save === 'saving') return;
    setFieldError(undefined);
    setGeneralError(null);
    setSave('saving');
    const result = await apiSetPassword(password);
    if (result.kind === 'ok') {
      setSave('saved');
      setPasswordValue('');
      queueMicrotask(() => savedRef.current?.focus());
      return;
    }
    if (result.kind === 'invalid') {
      setFieldError(result.errors.password ?? 'That password can’t be used.');
      setSave('error');
      return;
    }
    setGeneralError('We couldn’t save your password. Please try again.');
    setSave('error');
  }

  async function handleLogout(all: boolean) {
    const ok = all ? await logoutEverywhere() : await logout();
    if (ok) navigate('/login', { replace: true });
    else setGeneralError('We couldn’t sign you out. Please try again.');
  }

  if (load === 'loading') {
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-6 py-24" aria-live="polite">
        <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
        <p className="mt-4 font-serif text-lg text-ink">Loading your account…</p>
      </div>
    );
  }

  if (load === 'error') {
    return (
      <div className="mx-auto w-full max-w-md px-6 py-24">
        <p role="alert" className="flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
          We couldn’t load your account. Please refresh, or try signing in again.
        </p>
      </div>
    );
  }

  if (load === 'unauthenticated' || !me) return null; // redirecting

  return (
    <div className="mx-auto w-full max-w-md px-6 py-16 sm:py-20">
      <p className="font-display text-xs font-semibold uppercase tracking-[0.28em] text-accent-deep">
        Your account
      </p>
      <h1 className="mt-3 font-display text-3xl font-semibold uppercase tracking-[0.12em] text-ink sm:text-4xl">
        Account
      </h1>
      <p className="mt-4 font-serif text-lg leading-relaxed text-ink">
        Signed in as <span className="font-semibold">{me.email}</span>.
      </p>

      <section aria-labelledby="pw-heading" className="mt-10 border-t border-border pt-8">
        <h2 id="pw-heading" className="font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">
          Password
        </h2>
        <p className="mt-2 font-serif text-base leading-relaxed text-muted">
          A password is optional — the magic link always works. Set one if you’d rather sign in without
          waiting for email. At least 12 characters.
        </p>

        {generalError && (
          <p role="alert" className="mt-4 flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
            <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            {generalError}
          </p>
        )}

        {save === 'saved' ? (
          <p ref={savedRef} tabIndex={-1} className="mt-4 flex items-center gap-2 font-sans text-sm text-success">
            <CheckCircle2 aria-hidden className="h-4 w-4" />
            Password saved. You can now sign in with it.
          </p>
        ) : (
          <form onSubmit={submitPassword} noValidate className="mt-4 space-y-4">
            <PasswordField
              id="new-password"
              label="New password"
              autoComplete="new-password"
              value={password}
              onChange={setPasswordValue}
              error={fieldError}
              disabled={save === 'saving'}
            />
            <button
              type="submit"
              disabled={save === 'saving'}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-accent px-4 py-2.5 font-sans text-sm font-semibold text-bg transition-colors duration-150 ease-standard hover:bg-accent-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
            >
              {save === 'saving' && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
              Save password
            </button>
          </form>
        )}
      </section>

      <section aria-labelledby="out-heading" className="mt-10 border-t border-border pt-8">
        <h2 id="out-heading" className="font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">
          Sign out
        </h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => handleLogout(false)}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 font-sans text-sm font-medium text-ink transition-colors duration-150 ease-standard hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <LogOut aria-hidden className="h-4 w-4" />
            Sign out
          </button>
          <button
            type="button"
            onClick={() => handleLogout(true)}
            className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 font-sans text-sm font-medium text-muted transition-colors duration-150 ease-standard hover:bg-surface hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Sign out of all devices
          </button>
        </div>
      </section>
    </div>
  );
}
